import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import Select from 'core/components/Select';
import MaskedTextField from 'core/components/MaskedTextField';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import values from 'lodash/values';

type Props = {
  required?: boolean;
};

function validateNPINumber(value: string) {
  return isEmptyString(value) || value?.length === 10 || 'NPI Number must be 10 digits';
}

function validateTaxonomyCode(value: string) {
  return isEmptyString(value) || (value ?? '').replaceAll('_', '').length === 10 || 'Taxonomy Code must be 10 digits';
}

const npiNumberMask = [{
  mask: '0000000000',
  lazy: false,
  overwrite: true,
}];

export default function NPIFields({ required: _required = false }: Props) {
  const {
    control,
    trigger,
    formState: { isDirty },
  } = useFormContext();

  const npiValue = useWatch({ name: 'npi', control });

  const required = useMemo<boolean>(
    () => _required || values(npiValue).some(notEmptyString),
    [npiValue, _required],
  );

  useEffect(() => {
    if (isDirty) {
      trigger('npi');
    }
  }, [isDirty, npiValue]);

  return (
    <div className="flex gap-form-field w-full">
      <Controller
        name="npi.type"
        control={control}
        rules={{
          required: required && 'NPI Type is required',
        }}
        render={({ field, fieldState: { invalid } }) => (
          <Select<string>
            {...field}
            error={invalid}
            showErrorText={false}
            id="npi-type"
            className="flex-1 min-w-0 text-gray-500 text-xs"
            label="NPI Type"
            placeholder="Select"
            options={['NPI-1 Individual', 'NPI-2 Organization']}
            getLabel={(option: string) => option}
            getValue={(option: string) => option}
            required={required}
          />
        )}
      />
      <Controller
        name="npi.number"
        control={control}
        rules={{
          required: required && 'NPI Number is required',
          validate: validateNPINumber,
        }}
        render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
          <Wrapper
            id="npi-number"
            label="NPI Number"
            className="flex-1 min-w-0"
            showErrorText={false}
            required={required}
            error={invalid}
          >
            <MaskedTextField
              {...field}
              unmask
              mask={npiNumberMask}
              value={value}
              placeholder="NPI Number"
              onAccept={onChange}
            />
          </Wrapper>
        )}
      />
      <Controller
        name="npi.taxonomyCode"
        control={control}
        rules={{
          required: required && 'Taxonomy Code is required',
          validate: validateTaxonomyCode,
        }}
        render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
          <Wrapper
            id="npi-taxonomy-code"
            label="Taxonomy Code"
            className="flex-1 min-w-0"
            showErrorText={false}
            required={required}
            error={invalid}
          >
            <MaskedTextField
              {...field}
              unmask
              mask={[{
                mask: '*********{X}',
                lazy: false,
                overwrite: true,
              }]}
              value={value}
              placeholder="NPI Taxonomy Code"
              onAccept={(value: string) => {
                onChange(value === 'X' ? '' : value);
              }}
            />
          </Wrapper>
        )}
      />
    </div>
  );
}
